import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { IPlanInfo } from '../../../interfaces/plan-info.interface';
import { getPlanStartingMonthlyPrice, PlanService, roundToTwoDecimals } from '../../../services/plan.service';
import { UserService } from '../../users/users.service';

@Component({
  selector: 'app-plan-selector',
  templateUrl: './plan-selector.component.html',
  styleUrls: ['./plan-selector.component.css']
})
export class PlanSelectorComponent implements OnChanges, OnDestroy {
  @Input() plans: IPlanInfo[] = [];
  @Input() referredId: any = null;
  @Input() referredName = '';
  @Input() isBrandAmbassador = false;
  @Output() selectionChange = new EventEmitter<IPlanInfo | null>();
  @Output() continue = new EventEmitter<void>();

  readonly cards = [
    { type: 'START', name: 'Start', description: 'Para tus primeros clientes', rfcs: 'Hasta 5 RFCs', clients: 'Acceso para clientes incluido' },
    { type: 'PRO', name: 'Pro', description: 'Para crecer en equipo', rfcs: '10 RFCs incluidos', clients: 'Acceso para clientes incluido' },
    { type: 'FLEX', name: 'Flex', description: 'Más RFCs por contador', rfcs: '100 RFCs por contador', clients: 'Acceso para clientes opcional' }
  ];
  selectedType = 'START';
  recurrence = 1;
  users = 1;
  startUserHintHighlighted = false;
  rfcHintHighlighted = false;
  quote: IPlanInfo | null = null;
  private startUserHintTimeout: ReturnType<typeof setTimeout>;
  private rfcHintTimeout: ReturnType<typeof setTimeout>;
  private initialized = false;
  private destroyed = false;
  private readonly money = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
  private readonly cardMoney = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

  constructor(private planService: PlanService, private userService: UserService) {}

  ngOnChanges(): void {
    if (!this.plans.length) return;
    if (!this.initialized) {
      const initial = this.planService.resolveInitialPlan(this.plans);
      this.selectedType = initial?.plan_short_name || 'START';
      this.recurrence = initial?.recurrence || 1;
      this.initialized = true;
    }
    this.refreshQuote(false);
    // Publish after Angular has finished checking the containing form.
    Promise.resolve().then(() => {
      if (!this.destroyed) this.selectionChange.emit(this.quote);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.startUserHintTimeout);
    clearTimeout(this.rfcHintTimeout);
  }

  get selectedName(): string { return this.planService.formatPlanTypeLabel(this.selectedType); }
  get canContinue(): boolean { return !!this.quote && this.quote.id > 0 && getPlanStartingMonthlyPrice(this.quote) > 0 && Number.isFinite(this.quote.total_sale) && this.quote.total_sale > 0; }
  get period(): string { return this.recurrence === 12 ? 'año' : 'mes'; }
  get rfcs(): number {
    if (this.selectedType === 'FLEX') return (this.quote?.base_rfcs || 100) * this.users;
    return this.quote?.base_rfcs || (this.selectedType === 'START' ? 5 : 10);
  }

  monthlyPrice(type: string): number | null {
    const record = this.planService.findPlanByTypeAndRecurrence(this.plans, type, 1);
    const startingPrice = getPlanStartingMonthlyPrice(record);
    // Public Flex reference only; never create a synthetic checkout plan or ID.
    // Source: https://www.contalink.com/precios (2026-09-09).
    return startingPrice > 0 ? startingPrice : (type === 'FLEX' ? 990 : null);
  }

  get additionalUserPrice(): number {
    const record = this.planService.findPlanByTypeAndRecurrence(this.plans, this.selectedType, 1);
    return getPlanStartingMonthlyPrice(record) > 0 && record.user_price != null ? record.user_price : (this.selectedType === 'FLEX' ? 990 : 0);
  }

  get annualSavingsPercent(): number {
    const annual = this.planService.findPlanByTypeAndRecurrence(this.plans, this.selectedType, 12);
    if (getPlanStartingMonthlyPrice(annual) > 0) {
      return this.referredId != null || this.isBrandAmbassador ? 25 : annual.annual_discount_percentage || 0;
    }
    return this.selectedType === 'FLEX' ? 16.66 : 0;
  }

  isRecurrenceAvailable(value: number): boolean {
    if (this.selectedType === 'FLEX' && !this.plans.some(plan => plan.plan_short_name === 'FLEX' && getPlanStartingMonthlyPrice(plan) > 0)) return true;
    return getPlanStartingMonthlyPrice(this.planService.findPlanByTypeAndRecurrence(this.plans, this.selectedType, value)) > 0;
  }

  selectPlan(type: string): void {
    if (!this.cards.some(card => card.type === type)) return;
    clearTimeout(this.startUserHintTimeout);
    this.startUserHintHighlighted = false;
    clearTimeout(this.rfcHintTimeout);
    this.rfcHintHighlighted = false;
    this.selectedType = type;
    if (type === 'START') this.users = 1;
    if (!this.isRecurrenceAvailable(this.recurrence)) {
      this.recurrence = this.isRecurrenceAvailable(1) ? 1 : 12;
    }
    this.refreshQuote();
  }

  selectRecurrence(value: number): void {
    if (![1, 12].includes(value) || !this.isRecurrenceAvailable(value)) return;
    this.recurrence = value;
    this.refreshQuote();
  }

  changeUsers(delta: number): void {
    if (this.selectedType === 'START') {
      if (delta > 0) this.highlightStartUserHint();
      return;
    }
    this.users = Math.max(1, Math.min(100, this.users + delta));
    this.refreshQuote();
  }

  highlightStartUserHint(): void {
    if (this.selectedType !== 'START') return;
    clearTimeout(this.startUserHintTimeout);
    this.startUserHintHighlighted = true;
    this.startUserHintTimeout = setTimeout(() => this.startUserHintHighlighted = false, 3800);
  }

  highlightRfcHint(): void {
    clearTimeout(this.rfcHintTimeout);
    this.rfcHintHighlighted = true;
    this.rfcHintTimeout = setTimeout(() => this.rfcHintHighlighted = false, 3800);
  }

  private refreshQuote(publish = true): void {
    const record = this.planService.findPlanByTypeAndRecurrence(this.plans, this.selectedType, this.recurrence);
    this.quote = record ? this.userService.getCalculationTotalDiscount(
      { ...record, number_of_users: this.users }, this.referredId, this.isBrandAmbassador === true
    ) : null;
    if (!this.canContinue) this.quote = null;
    if (publish) this.selectionChange.emit(this.quote);
  }

  // Reference estimates stay separate from the API-backed checkout payload.
  get subtotal(): number {
    return this.quote ? this.quote.subtotal : (this.selectedType === 'FLEX' ? 990 * this.users * this.recurrence : 0);
  }
  get planAmount(): number {
    const price = this.quote ? getPlanStartingMonthlyPrice(this.quote) : (this.selectedType === 'FLEX' ? 990 : 0);
    return roundToTwoDecimals(price * this.recurrence);
  }
  get additionalUsersAmount(): number {
    return this.users > 1 ? roundToTwoDecimals(this.subtotal - this.planAmount) : 0;
  }
  get discount(): number {
    return this.quote ? this.quote.discount : (this.selectedType === 'FLEX' && this.recurrence === 12 ? 1980 * this.users : 0);
  }
  get iva(): number { return this.quote ? this.quote.iva : roundToTwoDecimals((this.subtotal - this.discount) * 0.16); }
  get total(): number { return this.quote ? this.quote.total_sale : roundToTwoDecimals(this.subtotal - this.discount + this.iva); }
  formatMoney(value: number): string { return this.money.format(value || 0); }
  formatCardMoney(value: number): string { return this.cardMoney.format(value || 0); }

  next(): void {
    if (this.canContinue) this.continue.emit();
  }
}
